/**
 * Lightweight fetch wrapper.
 *
 * - Centralizes the `/api` prefix, JSON encoding, and error extraction.
 * - Adds a current Microsoft Entra access token to every protected request.
 * - Retries an authentication rejection once after token renewal; never
 *   replays a request after a network failure or permission denial.
 */

import { getAccessToken, markSessionUnauthorized } from '../auth/auth.ts';
import { devIdentityHeader, isDevIdentity } from '../auth/dev-identity.ts';
import { flagOverrideHeader } from '../state/feature-flags.ts';
import { noteServerBuild } from '../state/app-build.ts';
import { noteForbiddenBody } from '../state/account-status.ts';
import { apiErrorMessage, statusMessage } from './errors.ts';

const API_PREFIX = '/api';

/**
 * A failed API call. `message` is written for people (see `errors.ts`); what
 * the server actually said is kept in `serverDetail` for the console and for error
 * reports, and `ref` is the server's reference for this request - the id on its
 * log lines.
 */
export class ApiError extends Error {
  status: number;
  ref: string | null;
  /** Raw, for logs only. Never show it: that is what `message` is for. */
  serverDetail: string | null;
  constructor(message: string, status: number, extra: { ref?: string | null; serverDetail?: string | null } = {}) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
    this.ref = extra.ref ?? null;
    this.serverDetail = extra.serverDetail ?? null;
  }
}

interface RequestOptions extends RequestInit {
  timeoutMs?: number;
}

async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
  const { timeoutMs = 30000, headers, ...rest } = opts;
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const token = await getAccessToken();
    const requestHeaders = new Headers(headers);
    requestHeaders.set('Content-Type', 'application/json');
    requestHeaders.set('Authorization', `Bearer ${token}`);
    // Only ever set when the backend reported authentication off, and ignored
    // by a backend that has it on. It names which development persona this
    // browser is signed in as.
    for (const [name, value] of Object.entries(devIdentityHeader())) {
      requestHeaders.set(name, value);
    }
    // `?ff=` overrides, so the server sees the same switches the browser does.
    // It honours them on local runs and for holders of `flags:write`, and
    // ignores them everywhere else - a link cannot switch a feature on.
    for (const [name, value] of Object.entries(flagOverrideHeader())) {
      requestHeaders.set(name, value);
    }
    const request: RequestInit = {
      headers: requestHeaders,
      signal: opts.signal ?? ctrl.signal,
      ...rest,
    };
    let response = await fetch(API_PREFIX + path, request);
    if (response.status === 401 && !isDevIdentity() && !request.signal?.aborted) {
      // Authentication rejects a 401 before the endpoint runs, so this one
      // retry is safe for writes too. Never retry network errors or other codes.
      let renewed: string;
      try {
        renewed = await getAccessToken({ forceRefresh: true });
      } catch (error) {
        markSessionUnauthorized();
        throw error;
      }
      if (request.signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
      requestHeaders.set('Authorization', `Bearer ${renewed}`);
      response = await fetch(API_PREFIX + path, request);
    }
    noteServerBuild(response.headers.get('X-App-Build'));
    return response;
  } finally {
    clearTimeout(t);
  }
}

async function throwResponseError(resp: Response): Promise<never> {
  if (resp.status === 401) markSessionUnauthorized();
  const text = await resp.text().catch(() => '');
  let detail: string | null = null;
  let ref: string | null = resp.headers.get('X-Request-Id');
  try {
    const body = JSON.parse(text) as { detail?: unknown; ref?: unknown };
    if (resp.status === 403) noteForbiddenBody(body as { detail?: string });
    if (typeof body.detail === 'string') detail = body.detail;
    if (typeof body.ref === 'string') ref = body.ref;
  } catch {
    // Not JSON: a load balancer's HTML page, or nothing. The status says enough.
  }
  throw new ApiError(apiErrorMessage(resp.status, detail, ref), resp.status, {
    ref,
    serverDetail: detail ?? (text.slice(0, 500) || null),
  });
}

export async function apiResponse(
  path: string,
  opts: RequestOptions = {},
): Promise<Response> {
  let resp: Response;
  try {
    resp = await rawFetch(path, opts);
  } catch (error) {
    const isAbort = error instanceof DOMException && error.name === 'AbortError';
    if (!(error instanceof TypeError) && !isAbort) throw error;
    // Our own timer aborts too: that is a slow server, not a missing one, and
    // saying "can't reach" sends people to check a connection that is fine.
    const timedOut = isAbort && !opts.signal?.aborted;
    throw new ApiError(statusMessage(timedOut ? 408 : 0), 0, {
      serverDetail: error instanceof Error ? error.message : null,
    });
  }

  if (!resp.ok) {
    return throwResponseError(resp);
  }
  return resp;
}

export async function api<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const resp = await apiResponse(path, opts);
  // A successful DELETE answers `204 No Content`, and parsing that as JSON
  // fails with "Unexpected end of JSON input" - an error for a request that
  // worked. Anything without a body resolves as undefined; the callers that use
  // it are typed `Promise<void>`.
  if (resp.status === 204 || resp.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await resp.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Build the absolute URL for an /api endpoint (used for download anchors). */
export function apiUrl(path: string): string {
  return API_PREFIX + path;
}
